import { minimalSetup } from "codemirror";
import { indentLess, indentMore } from "@codemirror/commands";
import { python } from "@codemirror/lang-python";
import { HighlightStyle, indentUnit, syntaxHighlighting } from "@codemirror/language";
import { EditorState, Prec, StateEffect, StateField } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  WidgetType,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from "@codemirror/view";
import { tags } from "@lezer/highlight";

import "./style.css";
import { visibleChatText } from "./chat";
import { DEFAULT_MODEL_ID, MODEL_OPTIONS, modelById } from "./config";
import { DEFAULT_INDENT, runTabCommand } from "./editor";
import { renderChatMarkdown } from "./markdown";
import type { ChatTurn, LocalQwen } from "./llm";

const initialCode = `import numpy as np

def kinetic_energy(mass, velocity):
    """Return kinetic energy for scalar or array velocity."""
    velocity = np.asarray(velocity)
    
`;

const kanagawaDragonHighlight = HighlightStyle.define([
  { tag: tags.comment, color: "#737c73", fontStyle: "italic" },
  { tag: [tags.keyword, tags.controlKeyword, tags.moduleKeyword], color: "#8992a7" },
  { tag: [tags.operator, tags.operatorKeyword], color: "#c4746e" },
  { tag: [tags.string, tags.special(tags.string)], color: "#8a9a7b" },
  { tag: [tags.number, tags.bool, tags.null], color: "#a292a3" },
  { tag: [tags.function(tags.variableName), tags.labelName], color: "#8ba4b0" },
  { tag: [tags.typeName, tags.className], color: "#8ea4a2" },
  { tag: [tags.propertyName, tags.attributeName], color: "#c4b28a" },
  { tag: [tags.meta, tags.atom], color: "#b6927b" },
  { tag: [tags.punctuation, tags.bracket], color: "#9e9b93" },
  { tag: tags.invalid, color: "#c4746e", textDecoration: "underline" },
]);

document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
  <main class="shell">
    <header class="topbar">
      <div class="brand">
        <h1>slopai</h1>
        <span class="eyebrow">local · WebGPU</span>
      </div>
      <button id="load-model" class="primary">Load local model</button>
    </header>

    <section id="model-card" class="model-card">
      <label class="model-picker">
        <span>Model</span>
        <select id="model-select">
          ${MODEL_OPTIONS.map((model) => `<option value="${model.id}"${model.id === DEFAULT_MODEL_ID ? " selected" : ""}>${model.label}</option>`).join("")}
        </select>
      </label>
      <span id="model-status"></span>
      <div id="device-note" class="device-note"></div>
      <div class="progress-track" aria-hidden="true"><div id="model-progress"></div></div>
    </section>

    <nav class="tabs" aria-label="Workspace">
      <button class="tab active" data-tab="editor">Editor</button>
      <button class="tab" data-tab="chat">Chat</button>
    </nav>

    <section id="editor-pane" class="pane active">
      <div class="pane-head">
        <div>
          <span class="file-dot"></span>
          <strong>main.py</strong>
        </div>
        <label class="toggle"><input id="fim-enabled" type="checkbox" checked /> FIM</label>
      </div>
      <div id="editor"></div>
      <div class="editor-status"><span id="fim-status">Load the model to enable FIM.</span><span>Tab accepts · Esc dismisses</span></div>
    </section>

    <section id="chat-pane" class="pane chat-pane">
      <div class="pane-head chat-head">
        <strong>Chat</strong>
        <span>Enter sends</span>
      </div>
      <div id="chat-log" class="chat-log" aria-live="polite">
        <div class="empty-chat"><strong>Local coding chat</strong><span>Short answers, no thinking mode, optional editor context.</span></div>
      </div>
      <form id="chat-form" class="chat-form">
        <textarea id="chat-input" rows="3" placeholder="Ask about the code… Enter sends · Shift+Enter adds a line" disabled></textarea>
        <div class="chat-controls">
          <label><input id="include-code" type="checkbox" checked /> Include editor</label>
          <div>
            <button id="clear-chat" class="quiet" type="button">Clear</button>
            <button id="send-chat" class="primary" type="submit" disabled>Send</button>
          </div>
        </div>
      </form>
    </section>
  </main>
`;

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

let qwen: LocalQwen | undefined;
const loadButton = byId<HTMLButtonElement>("load-model");
const modelSelect = byId<HTMLSelectElement>("model-select");
const statusText = byId<HTMLSpanElement>("model-status");
const progressBar = byId<HTMLDivElement>("model-progress");
const deviceNote = byId<HTMLDivElement>("device-note");
const fimEnabled = byId<HTMLInputElement>("fim-enabled");
const fimStatus = byId<HTMLSpanElement>("fim-status");
const chatForm = byId<HTMLFormElement>("chat-form");
const chatInput = byId<HTMLTextAreaElement>("chat-input");
const sendChat = byId<HTMLButtonElement>("send-chat");
const includeCode = byId<HTMLInputElement>("include-code");
const chatLog = byId<HTMLDivElement>("chat-log");

let suggestion = "";
let suggestionAt = 0;
let fimRequest = 0;
let chatBusy = false;
let chatTurns: ChatTurn[] = [];
let activeModelId: string | undefined;

function selectedModel() {
  return modelById(modelSelect.value);
}

function modelReady(): boolean {
  return qwen?.loaded === true && activeModelId === modelSelect.value;
}

function describeSelectedModel(): void {
  const model = selectedModel();
  statusText.textContent = `Downloads up to ${model.downloadGB.toFixed(3)} GB · ${model.note}.`;
  deviceNote.textContent = `Live model allocation: about ${(model.runtimeMB / 1000).toFixed(2)} GB plus browser overhead.`;
}

interface InlineSuggestion {
  text: string;
  position: number;
}

const setInlineSuggestion = StateEffect.define<InlineSuggestion | null>();

class InlineSuggestionWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }

  eq(other: InlineSuggestionWidget): boolean {
    return other.text === this.text;
  }

  toDOM(): HTMLElement {
    const widget = document.createElement("span");
    widget.className = "cm-ai-suggestion";
    widget.setAttribute("aria-label", `Qwen suggestion: ${this.text}. Press Tab to accept.`);

    const completion = document.createElement("span");
    completion.className = "cm-ai-suggestion-text";
    completion.textContent = this.text;

    const hint = document.createElement("span");
    hint.className = "cm-ai-suggestion-hint";
    hint.textContent = "Tab";
    hint.setAttribute("aria-hidden", "true");

    widget.append(completion, hint);
    return widget;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

const inlineSuggestionField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, transaction) {
    let next = decorations.map(transaction.changes);
    if (transaction.docChanged || transaction.selection) next = Decoration.none;
    for (const effect of transaction.effects) {
      if (!effect.is(setInlineSuggestion)) continue;
      const value = effect.value;
      next = value
        ? Decoration.set([
            Decoration.widget({
              widget: new InlineSuggestionWidget(value.text),
              side: 1,
            }).range(Math.min(value.position, transaction.newDoc.length)),
          ])
        : Decoration.none;
    }
    return next;
  },
  provide: (field) => EditorView.decorations.from(field),
});

function setSuggestion(value: string, position = editor.state.selection.main.head): void {
  suggestion = value;
  suggestionAt = position;
  editor.dispatch({
    effects: setInlineSuggestion.of(value ? { text: value, position } : null),
  });
}

function clearSuggestionWithoutDispatch(): void {
  suggestion = "";
  suggestionAt = editor.state.selection.main.head;
}

function acceptSuggestion(): boolean {
  if (!suggestion) return false;
  const insert = suggestion;
  const from = suggestionAt;
  setSuggestion("");
  editor.dispatch({
    changes: { from, insert },
    selection: { anchor: from + insert.length },
    scrollIntoView: true,
  });
  editor.focus();
  return true;
}

const editor = new EditorView({
  parent: byId<HTMLDivElement>("editor"),
  state: EditorState.create({
    doc: initialCode,
    extensions: [
      minimalSetup,
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      python(),
      syntaxHighlighting(kanagawaDragonHighlight),
      EditorState.tabSize.of(DEFAULT_INDENT.length),
      indentUnit.of(DEFAULT_INDENT),
      EditorView.lineWrapping,
      inlineSuggestionField,
      Prec.high(
        keymap.of([
          {
            key: "Tab",
            run: (view) => runTabCommand(acceptSuggestion, () => indentMore(view)),
            shift: indentLess,
          },
          {
            key: "Escape",
            run: () => {
              if (!suggestion) return false;
              setSuggestion("");
              fimStatus.textContent = "Suggestion dismissed.";
              return true;
            },
          },
        ]),
      ),
      EditorView.updateListener.of((update) => {
        if (update.docChanged || update.selectionSet) scheduleFim(true);
      }),
    ],
  }),
});

function scheduleFim(decorationAlreadyCleared = false): void {
  qwen?.interrupt();
  fimRequest += 1;
  if (decorationAlreadyCleared) clearSuggestionWithoutDispatch();
  else setSuggestion("");
  if (!modelReady() || !fimEnabled.checked || chatBusy) return;
  void requestFim();
}

async function requestFim(): Promise<void> {
  const request = ++fimRequest;
  const cursor = editor.state.selection.main.head;
  const snapshot = editor.state.doc.toString();
  const prefix = snapshot.slice(0, cursor);
  const suffix = snapshot.slice(cursor);
  fimStatus.textContent = "Completing locally…";
  try {
    if (!qwen) return;
    const result = await qwen.fim(prefix, suffix);
    if (request !== fimRequest || snapshot !== editor.state.doc.toString() || cursor !== editor.state.selection.main.head) return;
    setSuggestion(result, cursor);
    fimStatus.textContent = result ? "Completion ready." : "No useful completion.";
  } catch (error) {
    if (request !== fimRequest) return;
    fimStatus.textContent = readableError(error);
  }
}

function readableError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function inspectDevice(): Promise<void> {
  if (!("gpu" in navigator)) {
    deviceNote.textContent = "WebGPU is unavailable. Use current Safari 26+, Chrome, Edge, or Firefox with WebGPU enabled.";
    loadButton.disabled = true;
    return;
  }
  const estimate = await navigator.storage?.estimate?.();
  const available = estimate?.quota && estimate.usage ? (estimate.quota - estimate.usage) / 1e9 : undefined;
  const model = selectedModel();
  deviceNote.textContent = available
    ? `Browser storage available: about ${available.toFixed(1)} GB. Live model allocation: about ${(model.runtimeMB / 1000).toFixed(2)} GB plus overhead.`
    : `Live model allocation: about ${(model.runtimeMB / 1000).toFixed(2)} GB plus browser overhead.`;
}

loadButton.addEventListener("click", async () => {
  loadButton.disabled = true;
  loadButton.textContent = "Loading…";
  try {
    await navigator.storage?.persist?.();
    const { LocalQwen } = await import("./llm");
    qwen ??= new LocalQwen();
    const requestedModel = selectedModel();
    const gpu = await qwen.load(requestedModel.id, ({ fraction, text }) => {
      progressBar.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
      statusText.textContent = text;
    });
    activeModelId = requestedModel.id;
    progressBar.style.width = "100%";
    statusText.textContent = `${requestedModel.label} loaded on ${gpu}.`;
    loadButton.textContent = "Model ready";
    chatInput.disabled = false;
    sendChat.disabled = false;
    fimStatus.textContent = "FIM enabled. Suggestions start as you type.";
    scheduleFim();
  } catch (error) {
    statusText.textContent = readableError(error);
    loadButton.disabled = false;
    loadButton.textContent = "Retry model load";
  }
});

modelSelect.addEventListener("change", () => {
  qwen?.interrupt();
  if (activeModelId !== modelSelect.value) activeModelId = undefined;
  setSuggestion("");
  describeSelectedModel();
  loadButton.disabled = false;
  loadButton.textContent = qwen?.loaded ? "Switch model" : "Load local model";
  chatInput.disabled = true;
  sendChat.disabled = true;
  fimStatus.textContent = "Load the selected model to enable FIM.";
});

for (const tab of document.querySelectorAll<HTMLButtonElement>(".tab")) {
  tab.addEventListener("click", () => {
    const name = tab.dataset.tab;
    for (const candidate of document.querySelectorAll(".tab")) candidate.classList.toggle("active", candidate === tab);
    byId("editor-pane").classList.toggle("active", name === "editor");
    byId("chat-pane").classList.toggle("active", name === "chat");
    if (name === "editor") editor.requestMeasure();
  });
}

fimEnabled.addEventListener("change", () => {
  if (fimEnabled.checked) scheduleFim();
  else {
    qwen?.interrupt();
    setSuggestion("");
    fimStatus.textContent = "FIM paused.";
  }
});

function clippedEditorContext(): string {
  if (!includeCode.checked) return "";
  const text = editor.state.doc.toString();
  const cursor = editor.state.selection.main.head;
  return text.slice(Math.max(0, cursor - 3_500), Math.min(text.length, cursor + 1_500));
}

function appendMessage(role: "user" | "assistant", content: string): HTMLDivElement {
  chatLog.querySelector(".empty-chat")?.remove();
  const bubble = document.createElement("div");
  bubble.className = `message ${role}`;
  const label = document.createElement("span");
  label.textContent = role === "user" ? "You" : "Qwen";
  const body = document.createElement("div");
  body.className = "message-body";
  if (role === "assistant") body.innerHTML = renderChatMarkdown(content);
  else body.textContent = content;
  bubble.append(label, body);
  chatLog.append(bubble);
  chatLog.scrollTop = chatLog.scrollHeight;
  return bubble;
}

chatForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  const prompt = chatInput.value.trim();
  if (!prompt || !modelReady() || !qwen || chatBusy) return;
  chatBusy = true;
  qwen.interrupt();
  fimRequest += 1;
  setSuggestion("");
  chatInput.value = "";
  chatInput.disabled = true;
  sendChat.disabled = true;
  appendMessage("user", prompt);
  chatTurns.push({ role: "user", content: prompt });
  const assistant = appendMessage("assistant", "");
  const body = assistant.querySelector<HTMLDivElement>(".message-body")!;
  let rawAnswer = "";
  let answer = "";
  try {
    for await (const chunk of qwen.chat(chatTurns, clippedEditorContext())) {
      rawAnswer += chunk;
      answer = visibleChatText(rawAnswer);
      body.innerHTML = renderChatMarkdown(answer);
      chatLog.scrollTop = chatLog.scrollHeight;
    }
    chatTurns.push({ role: "assistant", content: answer });
  } catch (error) {
    if (answer) body.innerHTML = renderChatMarkdown(answer);
    else body.textContent = readableError(error);
  } finally {
    chatBusy = false;
    chatInput.disabled = false;
    sendChat.disabled = false;
    chatInput.focus();
  }
});

chatInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    chatForm.requestSubmit();
  }
});

byId<HTMLButtonElement>("clear-chat").addEventListener("click", () => {
  qwen?.interrupt();
  chatTurns = [];
  chatLog.replaceChildren();
  const empty = document.createElement("div");
  empty.className = "empty-chat";
  empty.innerHTML = "<strong>Local coding chat</strong><span>Conversation cleared.</span>";
  chatLog.append(empty);
});

describeSelectedModel();
void inspectDevice();
